import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
  type SliceCaseReducers,
} from '@reduxjs/toolkit';
import { DEFAULT_ERROR, NOT_AUTHORIZED_USER_INVITE, NOT_AUTHORIZED_USER_VIEW } from '../../const/messageCodes';
import { OwnerUser } from '../../models/OwnerUser';
import { reduxErrorHandler } from '../../utils/reduxError';
import { AppThunk } from '../store';
import { listOwnerUsers, createUser, UserData, ErrorResponse } from '../../apis/EnterpriseApiV1';
import { AxiosError } from 'axios';

type OwnerUsersState = {
  isLoading: boolean;
  error: null | string | Record<string, string>;
} & EntityState<OwnerUser>;

const ownerUsersAdapter = createEntityAdapter<OwnerUser>();

export const ownerUsersSlice = createSlice<OwnerUsersState, SliceCaseReducers<OwnerUsersState>, 'ownerUsers'>({
  name: 'ownerUsers',
  initialState: ownerUsersAdapter.getInitialState({
    isLoading: false,
    error: null,
    entities: null,
  }),
  reducers: {
    ownerUserRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    ownerUsersRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    ownerUserReceived(state, action: PayloadAction<OwnerUser>) {
      ownerUsersAdapter.upsertOne(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    ownerUsersReceived(state, action: PayloadAction<OwnerUser[]>) {
      ownerUsersAdapter.setAll(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    errorReceived(state, action: PayloadAction<string | Record<string, string>>) {
      state.error = action.payload;
      state.isLoading = false;
    },
  },
});

export const { reducer: ownerUsersReducer } = ownerUsersSlice;

// Selectors
export const { selectAll, selectById, selectTotal } = ownerUsersAdapter.getSelectors();

export const { ownerUsersRequested, ownerUserRequested, ownerUsersReceived, ownerUserReceived, errorReceived } =
  ownerUsersSlice.actions;

// Async actions
export const fetchOwnerUsers = (enterpriseId: number): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(ownerUsersRequested(undefined));
      const ownerUsers = await listOwnerUsers(enterpriseId);

      dispatch(ownerUsersReceived(ownerUsers));
    } catch (e: any) {
      reduxErrorHandler(e, dispatch, errorReceived, NOT_AUTHORIZED_USER_VIEW);
    }
  };
};

export const createOwnerUser = (enterpriseId: number, userData: UserData): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(ownerUserRequested(undefined));

      const ownerUser = await createUser(enterpriseId, userData);

      dispatch(ownerUserReceived(ownerUser));
    } catch (e: any) {
      const error: AxiosError<ErrorResponse> = e;
      if (!error.response) {
        return dispatch(errorReceived({ messageCode: DEFAULT_ERROR }));
      }
      const { data } = error.response;

      reduxErrorHandler(e, dispatch, errorReceived, NOT_AUTHORIZED_USER_INVITE);

      return Promise.reject(data.error);
    }
  };
};
